import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, TrendingUp, AlertTriangle, Users, Award } from "lucide-react";

/**
 * Manager & SuperAdmin leaderboard: total leads · won · active · overdue · pipeline · conversion.
 * Row sorting: default is (wins desc, pipeline desc) — same as the backend response.
 */
const FMT = (n) => Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—";
const money = (n) => n ? `₹${(n >= 100000 ? (n / 100000).toFixed(1) + "L" : (n / 1000).toFixed(1) + "K")}` : "₹0";

export default function TeamPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/analytics/team-performance");
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const rows = data?.members || [];
  const totals = rows.reduce((acc, r) => ({
    total: acc.total + r.total, won: acc.won + r.won, active: acc.active + r.active,
    overdue: acc.overdue + r.overdue, pipeline: acc.pipeline + r.pipeline_value,
  }), { total: 0, won: 0, active: 0, overdue: 0, pipeline: 0 });

  const top = rows[0];

  return (
    <div className="space-y-6" data-testid="team-performance-page">
      <header>
        <p className="text-xs uppercase tracking-widest text-slate-500">People</p>
        <h1 className="text-xl font-semibold mt-1 text-slate-900">Team Performance</h1>
        <p className="text-slate-500 mt-1 text-sm">Every direct report and team member — ranked by wins & pipeline.</p>
      </header>

      {loading ? (
        <p className="text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="solid-panel p-12 text-center">
          <Users className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">No one on your team yet.</p>
          <p className="text-xs text-slate-400 mt-1">Ask SuperAdmin to set members to "Reports To → you".</p>
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { label: "Members", value: rows.length, color: "text-indigo-600 bg-indigo-50", Icon: Users },
              { label: "Total leads", value: FMT(totals.total), color: "text-slate-700 bg-slate-100", Icon: TrendingUp },
              { label: "Won", value: FMT(totals.won), color: "text-emerald-600 bg-emerald-50", Icon: Trophy },
              { label: "Overdue", value: FMT(totals.overdue), color: "text-rose-600 bg-rose-50", Icon: AlertTriangle },
              { label: "Pipeline value", value: money(totals.pipeline), color: "text-violet-600 bg-violet-50", Icon: Award },
            ].map((k) => (
              <div key={k.label} className="solid-panel p-4">
                <div className={`w-9 h-9 rounded-lg ${k.color} flex items-center justify-center mb-2`}>
                  <k.Icon className="w-4 h-4" />
                </div>
                <p className="text-xs text-slate-500 uppercase tracking-widest">{k.label}</p>
                <p className="text-2xl font-semibold text-slate-900 mt-0.5">{k.value}</p>
              </div>
            ))}
          </div>

          {/* Leaderboard */}
          <div className="solid-panel overflow-hidden" data-testid="team-leaderboard">
            <table className="w-full text-sm">
              <thead className="bg-slate-100/70 text-slate-600 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-5 py-3">#</th>
                  <th className="text-left px-5 py-3">Member</th>
                  <th className="text-right px-5 py-3">Total</th>
                  <th className="text-right px-5 py-3">Won</th>
                  <th className="text-right px-5 py-3">Active</th>
                  <th className="text-right px-5 py-3">Overdue</th>
                  <th className="text-right px-5 py-3">Pipeline</th>
                  <th className="text-right px-5 py-3">Conv %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.user_id} className={`border-t border-slate-100 ${i === 0 ? "bg-amber-50/40" : ""}`} data-testid={`team-row-${r.user_id}`}>
                    <td className="px-5 py-3 font-semibold text-slate-500">
                      {i === 0 ? <Trophy className="w-4 h-4 text-amber-500 inline" /> : i + 1}
                    </td>
                    <td className="px-5 py-3">
                      <p className="font-medium text-slate-900">{r.name}</p>
                      <p className="text-[11px] text-slate-500 capitalize">{r.role}</p>
                    </td>
                    <td className="px-5 py-3 text-right">{FMT(r.total)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-emerald-600">{FMT(r.won)}</td>
                    <td className="px-5 py-3 text-right">{FMT(r.active)}</td>
                    <td className={`px-5 py-3 text-right ${r.overdue > 0 ? "text-rose-600 font-semibold" : "text-slate-500"}`}>{FMT(r.overdue)}</td>
                    <td className="px-5 py-3 text-right">{money(r.pipeline_value)}</td>
                    <td className="px-5 py-3 text-right">{r.conversion}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {top && (
            <p className="text-xs text-slate-500 text-center">
              🏆 <span className="font-semibold">{top.name}</span> is leading with <span className="text-emerald-600 font-semibold">{top.won} wins</span> and <span className="text-violet-600 font-semibold">{money(top.pipeline_value)}</span> in pipeline.
            </p>
          )}
        </>
      )}
    </div>
  );
}
